import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useQuery } from '@tanstack/react-query';
import { calculate8Level } from '../../utils/cbcUtils';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import FilterDropdown from '../common/FilterDropdown';
import {
    UserGroupIcon,
    CalendarIcon,
    ChartBarIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    EyeIcon,
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    Squares2X2Icon,
    ListBulletIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    DocumentArrowDownIcon,
    ScaleIcon,
    ArrowRightIcon,
    XMarkIcon,
    AcademicCapIcon
} from '@heroicons/react/24/outline';
import { getLevelData, CBC_LEVELS } from '../../utils/cbcUtils';
import CompareControls from '../dashboard/CompareControls';
import ExportReportModal from '../common/ExportReportModal';
import GradeBook from './GradeBook';
import TeacherAiAssistantDrawer from './TeacherAiAssistantDrawer';

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie
} from 'recharts';

const StatCard = ({ label, value, icon: Icon, badge, color = 'indigo', onClick }) => (
    <div 
        className={`bg-white rounded-2xl md:rounded-3xl shadow-sm border border-slate-100 p-3 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-5 transition-all h-full ${onClick ? 'cursor-pointer hover:shadow-md hover:border-indigo-100 hover:bg-indigo-50/10' : 'hover:shadow-md'}`}
        onClick={onClick}
    >
        <div className={`h-10 w-10 md:h-14 md:w-14 rounded-xl md:rounded-2xl bg-${color}-50 text-${color}-600 flex items-center justify-center shrink-0`}>
            <Icon className="h-5 w-5 md:h-7 md:w-7" />
        </div>
        <div className="min-w-0 flex-1 w-full">
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-black truncate">{label}</p>
            <p className="text-lg md:text-3xl font-black text-[#18216D] leading-none mt-1 truncate">{value}</p>
            {badge && <p className="text-[9px] md:text-[10px] font-bold text-[#FFC425] bg-[#FFC425]/10 px-2 py-0.5 rounded-lg mt-1 md:mt-2 inline-block uppercase tracking-tighter truncate max-w-full">{badge}</p>}
        </div>
    </div>
);

const HodDashboard = () => {
    const { get } = useApi();
    const { showToast, performanceBands, is_principal } = useAppState();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [selectedLevelId, setSelectedLevelId] = useState(null);
    const [selectedGradeId, setSelectedGradeId] = useState(null);
    const [selectedStreamId, setSelectedStreamId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTerm, setSelectedTerm] = useState(null);
    const [exportStudentId, setExportStudentId] = useState(null);
    const [riskViewMode, setRiskViewMode] = useState('grid'); // 'grid' | 'table'
    const [expandedBands, setExpandedBands] = useState({});
    const [streamComparisonFilters, setStreamComparisonFilters] = useState({
        target_term_id: '',
        compare_term_id: '',
        target_year_id: '',
        compare_year_id: ''
    });
    const [perfComparisonFilters, setPerfComparisonFilters] = useState({
        target_term_id: '',
        compare_term_id: '',
        target_year_id: '',
        compare_year_id: ''
    });
    const [assessmentFilter, setAssessmentFilter] = useState('combined');
    const itemsPerPage = 10;

    // 0. Fetch terms for filtering
    const { data: terms } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
        staleTime: 30 * 60 * 1000,
    });

    useEffect(() => {
        if (terms?.length > 0 && !selectedTerm) {
            const active = terms.find(t => t.is_current) || terms[0];
            if (active) {
                setSelectedTerm(active.id.toString());
            }
        }
    }, [terms, selectedTerm]);

    // 1. Fetch managed levels
    const { data: myLevels, isLoading: levelsLoading } = useQuery({
        queryKey: ['my-school-levels'],
        queryFn: () => get('/api/cbc/school-levels/my_levels/'),
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        if (myLevels?.[0] && !selectedLevelId) {
            setSelectedLevelId(myLevels[0].id);
        }
    }, [myLevels, selectedLevelId]);

    // 2. Fetch dashboard composite data (stats + student roster) in ONE backend call
    const { data: dashData, isLoading: dashLoading } = useQuery({
        queryKey: ['hod-dashboard-composite', selectedLevelId, selectedGradeId, selectedStreamId, selectedTerm, assessmentFilter],
        queryFn: async () => {
            if (!selectedLevelId || !selectedTerm) return { stats: null, students: [], events: [] };
            
            const params = new URLSearchParams();
            if (selectedStreamId) params.append('stream_id', selectedStreamId);
            if (selectedGradeId) params.append('grade_id', selectedGradeId);
            if (selectedTerm) params.append('term_id', selectedTerm);
            if (assessmentFilter && assessmentFilter !== 'combined') params.append('exam_type', assessmentFilter);
            
            const queryPart = params.toString() ? `?${params.toString()}` : '';
            // Single composite call replaces dashboard_stats + student_list (eliminates duplicate get_bulk_student_performance)
            const [compositeData, eventsData] = await Promise.all([
                get(`/api/cbc/school-levels/${selectedLevelId}/hod_composite/${queryPart}`),
                get(`/api/cbc/school-levels/${selectedLevelId}/upcoming_events/`),
            ]);
            return { stats: compositeData.stats, students: compositeData.students, events: eventsData };
        },
        enabled: !!selectedLevelId && !!selectedTerm && selectedTerm !== 'undefined',
        staleTime: 5 * 60 * 1000,
        onError: () => showToast('Failed to load department data', 'error'),
    });

    const [selectedClassForGradebook, setSelectedClassForGradebook] = useState(null);
    const [modalBandCounts, setModalBandCounts] = useState({ EE: 0, ME: 0, AE: 0, BE: 0 });

    // Fetch Teacher Classes & Assessment Status (compact oversight widget)
    const { data: teacherClassesData, isLoading: teacherClassesLoading } = useQuery({
        queryKey: ['hod-teacher-classes-status', selectedLevelId, selectedTerm],
        queryFn: () => get(`/api/cbc/school-levels/${selectedLevelId}/teacher_classes_status/${selectedTerm ? `?term_id=${selectedTerm}` : ''}`),
        enabled: !is_principal && !!selectedLevelId && !!selectedTerm && selectedTerm !== 'undefined',
        staleTime: 60 * 1000,
    });

    // Filter and sort teacher classes based on selected grade and stream from Grades & Streams card
    const filteredTeacherClasses = useMemo(() => {
        let list = teacherClassesData || [];
        if (selectedGradeId) {
            list = list.filter(c => String(c.grade_level_id) === String(selectedGradeId));
        }
        if (selectedStreamId) {
            list = list.filter(c => {
                if (c.stream_ids && c.stream_ids.length > 0) {
                    return c.stream_ids.some(sid => String(sid) === String(selectedStreamId));
                }
                return false;
            });
        }
        return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [teacherClassesData, selectedGradeId, selectedStreamId]);

    const completedClassesCount = useMemo(() => {
        return filteredTeacherClasses.filter(c => c.status === 'Complete' || c.grading_progress_pct >= 100).length;
    }, [filteredTeacherClasses]);

    // ────────────────────────────────────────────────────────────────────────
    // 3. Performance Chart — Single Shared Target Query
    //    Both the Stream chart and the Subject chart use the same performance_matrix
    //    endpoint with identical params on initial load. One shared query eliminates
    //    the duplicate network round-trip.
    // ────────────────────────────────────────────────────────────────────────

    // Shared target query (used by BOTH stream and subject performance charts)
    const sharedTargetTermId = perfComparisonFilters.target_term_id || streamComparisonFilters.target_term_id || selectedTerm;
    const sharedTargetYearId = perfComparisonFilters.target_year_id || streamComparisonFilters.target_year_id || '';

    const { data: sharedTargetData, isLoading: sharedTargetLoading } = useQuery({
        queryKey: ['hod-matrix-target', selectedLevelId, sharedTargetTermId, sharedTargetYearId, assessmentFilter],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.append('school_level', selectedLevelId);
            if (sharedTargetTermId) params.append('term_id', sharedTargetTermId);
            if (sharedTargetYearId) params.append('year_id', sharedTargetYearId);
            if (assessmentFilter && assessmentFilter !== 'combined') params.append('exam_type', assessmentFilter);
            return get(`/api/teacher/class-teacher/performance_matrix/?${params.toString()}`);
        },
        enabled: !!selectedLevelId && (!!selectedTerm || !!sharedTargetTermId),
        staleTime: 5 * 60 * 1000,
    });

    // Alias so chart derivation logic below is unchanged
    const streamTargetData = sharedTargetData;
    const perfTargetData = sharedTargetData;
    const streamTargetLoading = sharedTargetLoading;
    const perfTargetLoading = sharedTargetLoading;

    // Stream Dataset B (only when user explicitly enables comparison)
    const isStreamComparisonEnabled = !!(streamComparisonFilters.compare_term_id || streamComparisonFilters.compare_year_id);
    const { data: streamCompareData, isLoading: streamCompareLoading } = useQuery({
        queryKey: ['hod-stream-compare', selectedLevelId, streamComparisonFilters.compare_term_id, streamComparisonFilters.compare_year_id],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.append('school_level', selectedLevelId);
            if (streamComparisonFilters.compare_term_id) params.append('term_id', streamComparisonFilters.compare_term_id);
            if (streamComparisonFilters.compare_year_id) params.append('year_id', streamComparisonFilters.compare_year_id);
            return get(`/api/teacher/class-teacher/performance_matrix/?${params.toString()}`);
        },
        enabled: !!selectedLevelId && isStreamComparisonEnabled,
        staleTime: 5 * 60 * 1000,
    });

    // Perf Dataset B (only when user explicitly enables comparison)
    const isPerfComparisonEnabled = !!(perfComparisonFilters.compare_term_id || perfComparisonFilters.compare_year_id);
    const { data: perfCompareData, isLoading: perfCompareLoading } = useQuery({
        queryKey: ['hod-perf-compare', selectedLevelId, perfComparisonFilters.compare_term_id, perfComparisonFilters.compare_year_id],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.append('school_level', selectedLevelId);
            if (perfComparisonFilters.compare_term_id) params.append('term_id', perfComparisonFilters.compare_term_id);
            if (perfComparisonFilters.compare_year_id) params.append('year_id', perfComparisonFilters.compare_year_id);
            return get(`/api/teacher/class-teacher/performance_matrix/?${params.toString()}`);
        },
        enabled: !!selectedLevelId && isPerfComparisonEnabled,
        staleTime: 5 * 60 * 1000,
    });

    // Derive per-subject class averages with robust comparison and merging
    const chartData = useMemo(() => {
        if (!perfTargetData?.subjects) return [];

        const targetSubjects = perfTargetData.subjects;
        const compareSubjects = perfCompareData?.subjects || [];
        
        // Merge all subjects from both periods to handle partial offerings
        const allSubjectCodes = Array.from(new Set([
            ...targetSubjects.map(s => s.code),
            ...compareSubjects.map(s => s.code)
        ]));

        const allSubjectNames = {};
        targetSubjects.forEach(s => allSubjectNames[s.code] = s.name);
        compareSubjects.forEach(s => allSubjectNames[s.code] = s.name);

        return allSubjectCodes.map(code => {
            // Find overall subject average across all students in target period
            const targetScores = (perfTargetData.students || [])
                .map(s => s.subject_scores[code]?.percentage)
                .filter(p => p !== null && p !== undefined);
            
            const targetAvg = targetScores.length > 0 
                ? targetScores.reduce((a, b) => a + b, 0) / targetScores.length 
                : 0;

            const res = {
                learning_area: allSubjectNames[code],
                target_score: Math.round(targetAvg),
                has_target: targetScores.length > 0
            };

            if (isPerfComparisonEnabled) {
                const compareScores = (perfCompareData?.students || [])
                    .map(s => s.subject_scores[code]?.percentage)
                    .filter(p => p !== null && p !== undefined);
                
                const compareAvg = compareScores.length > 0 
                    ? compareScores.reduce((a, b) => a + b, 0) / compareScores.length 
                    : 0;
                
                res.compare_score = Math.round(compareAvg);
                res.has_compare = compareScores.length > 0;
            }

            return res;
        }).sort((a, b) => b.target_score - a.target_score);
    }, [perfTargetData, perfCompareData, isPerfComparisonEnabled]);

    // Derive per-stream averages with robust comparison and merging
    const streamChartData = useMemo(() => {
        const streamMap = new Map();
        
        // Target Period
        if (streamTargetData?.students) {
            streamTargetData.students.forEach(student => {
                const key = (student.grade && student.stream && student.stream !== 'N/A') ? `${student.grade.replace('Grade ', '')}${student.stream}` : student.grade || student.stream || 'Unknown';
                if (!streamMap.has(key)) streamMap.set(key, { target_scores: [], compare_scores: [] });
                const scores = Object.values(student.subject_scores || {}).map(s => s.percentage).filter(p => p != null);
                if (scores.length > 0) streamMap.get(key).target_scores.push(scores.reduce((a, b) => a + b, 0) / scores.length);
            });
        }
        
        // Compare Period
        if (isStreamComparisonEnabled && streamCompareData?.students) {
            streamCompareData.students.forEach(student => {
                const key = (student.grade && student.stream && student.stream !== 'N/A') ? `${student.grade.replace('Grade ', '')}${student.stream}` : student.grade || student.stream || 'Unknown';
                if (!streamMap.has(key)) streamMap.set(key, { target_scores: [], compare_scores: [] });
                const scores = Object.values(student.subject_scores || {}).map(s => s.percentage).filter(p => p != null);
                if (scores.length > 0) streamMap.get(key).compare_scores.push(scores.reduce((a, b) => a + b, 0) / scores.length);
            });
        }
        
        return Array.from(streamMap.entries())
            .map(([stream, data]) => {
                const tAvg = data.target_scores.length > 0 ? Math.round(data.target_scores.reduce((a, b) => a + b, 0) / data.target_scores.length) : 0;
                const res = {
                    stream,
                    target_score: tAvg,
                    has_target: data.target_scores.length > 0,
                    student_count: data.target_scores.length // based on target period
                };
                if (isStreamComparisonEnabled) {
                    const cAvg = data.compare_scores.length > 0 ? Math.round(data.compare_scores.reduce((a, b) => a + b, 0) / data.compare_scores.length) : 0;
                    res.compare_score = cAvg;
                    res.has_compare = data.compare_scores.length > 0;
                    res.compare_student_count = data.compare_scores.length;
                }
                return res;
            })
            .sort((a, b) => b.target_score - a.target_score);
    }, [streamTargetData, streamCompareData, isStreamComparisonEnabled]);

    const stats = dashData?.stats ?? null;
    const students = useMemo(() => dashData?.students ?? [], [dashData?.students]);
    const events = dashData?.events ?? [];

    // Combined Grade & Stream dropdown options (one dropdown for both HOD and Principal)
    const combinedGradeStreamOptions = useMemo(() => {
        const options = [];
        if (is_principal) {
            // Principal: iterate all levels → all their grades → all streams
            for (const level of (myLevels || [])) {
                // Use stats.grades when this level is currently selected (has fresh stream data)
                const grades = (String(level.id) === String(selectedLevelId) && stats?.grades?.length > 0)
                    ? stats.grades
                    : (level.grades || []);
                for (const grade of grades) {
                    options.push({ name: grade.name, value: `g-${grade.id}-l-${level.id}` });
                    for (const stream of (grade.streams || [])) {
                        options.push({
                            name: `${grade.name} – Stream ${stream.name}`,
                            value: `s-${stream.id}-g-${grade.id}-l-${level.id}`
                        });
                    }
                }
            }
        } else {
            // HOD: only their managed level's grades from stats
            for (const grade of (stats?.grades || [])) {
                options.push({ name: grade.name, value: `g-${grade.id}` });
                for (const stream of (grade.streams || [])) {
                    options.push({
                        name: `${grade.name} – Stream ${stream.name}`,
                        value: `s-${stream.id}-g-${grade.id}`
                    });
                }
            }
        }
        return options;
    }, [is_principal, myLevels, stats?.grades, selectedLevelId]);

    // Derive the current combined value from existing grade/stream/level state
    const combinedGradeStreamValue = useMemo(() => {
        if (!selectedGradeId) return '';
        if (selectedStreamId) {
            if (is_principal) {
                const level = myLevels?.find(l => (l.grades || []).some(g => g.id === selectedGradeId));
                return `s-${selectedStreamId}-g-${selectedGradeId}-l-${level?.id || ''}`;
            }
            return `s-${selectedStreamId}-g-${selectedGradeId}`;
        }
        if (is_principal) {
            const level = myLevels?.find(l => (l.grades || []).some(g => g.id === selectedGradeId));
            return `g-${selectedGradeId}-l-${level?.id || ''}`;
        }
        return `g-${selectedGradeId}`;
    }, [selectedGradeId, selectedStreamId, is_principal, myLevels]);

    // Handler for combined Grade & Stream dropdown
    const handleCombinedChange = useCallback((val) => {
        if (!val) {
            setSelectedGradeId(null);
            setSelectedStreamId(null);
            setCurrentPage(1);
            return;
        }
        const streamMatch = val.match(/^s-(\d+)-g-(\d+)(?:-l-(\d+))?$/);
        if (streamMatch) {
            setSelectedStreamId(Number(streamMatch[1]));
            setSelectedGradeId(Number(streamMatch[2]));
            if (streamMatch[3] && is_principal) setSelectedLevelId(Number(streamMatch[3]));
            setCurrentPage(1);
            return;
        }
        const gradeMatch = val.match(/^g-(\d+)(?:-l-(\d+))?$/);
        if (gradeMatch) {
            setSelectedGradeId(Number(gradeMatch[1]));
            setSelectedStreamId(null);
            if (gradeMatch[2] && is_principal) setSelectedLevelId(Number(gradeMatch[2]));
            setCurrentPage(1);
        }
    }, [is_principal]);
    
    // Pending Discipline count for HOD alert badge
    const { data: hodDisciplineRecords = [] } = useQuery({
        queryKey: ['hod-discipline-queue-badge'],
        queryFn: () => get('/api/discipline/records/hod-queue/'),
        staleTime: 30 * 1000,
    });
    const pendingDisciplineCount = useMemo(() => {
        return Array.isArray(hodDisciplineRecords)
            ? hodDisciplineRecords.filter(r => ['pending_review', 'escalated'].includes(r.status)).length
            : 0;
    }, [hodDisciplineRecords]);

    // Filter & Paginate Students
    const { paginatedStudents, totalPages } = useMemo(() => {
        const lowerTerm = searchTerm.toLowerCase();
        const filtered = students
            .filter(student =>
                student.name.toLowerCase().includes(lowerTerm) ||
                student.student_id.toLowerCase().includes(lowerTerm) ||
                student.grade.toLowerCase().includes(lowerTerm)
            )
            .sort((a, b) => (b.performance || 0) - (a.performance || 0));

        const pages = Math.ceil(filtered.length / itemsPerPage);
        const paginated = filtered.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
        return { paginatedStudents: paginated, totalPages: pages };
    }, [students, searchTerm, currentPage]);

    // Attention List Logic — flags low attendance (<75%) or AE2/BE1/BE2 performance level
    const studentsRequiringAttention = useMemo(() => {
        const AT_RISK_PERFORMANCE_LEVELS = ['AE', 'BE'];
        return students.filter(s =>
            s.attendance < 75 ||
            (s.performance > 0 && AT_RISK_PERFORMANCE_LEVELS.some(lvl => s.performance_level?.startsWith(lvl)))
        );
    }, [students]);

    // Pie Chart Data Logic
    const pieChartData = useMemo(() => {
        const bands = {
            EE: { label: 'Exceeding Expectations', count: 0, subbands: { EE1: 0, EE2: 0, EE3: 0 } },
            ME: { label: 'Meeting Expectations', count: 0, subbands: { ME1: 0, ME2: 0, ME3: 0 } },
            AE: { label: 'Approaching Expectations', count: 0, subbands: { AE1: 0, AE2: 0, AE3: 0 } },
            BE: { label: 'Below Expectations', count: 0, subbands: { BE1: 0, BE2: 0, BE3: 0 } }
        };

        students.forEach(s => {
            if (!s.performance_level) return;
            const bandCode = s.performance_level.substring(0, 2);
            if (bands[bandCode]) {
                bands[bandCode].count++;
                if (bands[bandCode].subbands[s.performance_level] !== undefined) {
                    bands[bandCode].subbands[s.performance_level]++;
                } else {
                    bands[bandCode].subbands[s.performance_level] = 1;
                }
            }
        });

        return Object.entries(bands)
            .filter(([_, data]) => data.count > 0)
            .map(([code, data]) => {
                const levelData = CBC_LEVELS.find(l => l.code === code) || CBC_LEVELS[3];
                return {
                    name: code,
                    label: data.label,
                    value: data.count,
                    color: levelData.hex,
                    bgClass: levelData.bg,
                    textClass: levelData.color,
                    borderClass: levelData.border,
                    subbands: data.subbands
                };
            });
    }, [students]);

    if (levelsLoading || (selectedLevelId && dashLoading)) return <LoadingSpinner fullScreen />;

    if (!myLevels || myLevels.length === 0) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-[2.5rem] shadow-xl p-12 text-center max-w-md">
                    <div className="h-20 w-20 bg-slate-100 rounded-[1.5rem] flex items-center justify-center mx-auto mb-6">
                        <ExclamationTriangleIcon className="w-10 h-10 text-slate-400" />
                    </div>
                    <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tight mb-3">No HOD Assignments</h2>
                    <p className="text-sm text-slate-500 mb-8">You haven't been assigned as a {is_principal ? 'Principal' : 'Head of Department'}. Please contact administration.</p>
                    <button onClick={() => navigate('/dashboard')} className="px-6 py-3 bg-[#18216D] text-white rounded-xl font-black text-xs uppercase tracking-widest">Back Home</button>
                </div>
            </div>
        );
    }

    const streamTargetLabel = terms?.find(t => t.id.toString() === (streamComparisonFilters.target_term_id || selectedTerm))?.name || 'Target Period';
    const streamCompareLabel = streamComparisonFilters.compare_term_id 
        ? (terms?.find(t => t.id.toString() === streamComparisonFilters.compare_term_id.toString())?.name || 'Comparison Term')
        : (streamComparisonFilters.compare_year_id ? 'Comparison Year' : 'Compare');

    const perfTargetLabel = terms?.find(t => t.id.toString() === (perfComparisonFilters.target_term_id || selectedTerm))?.name || 'Target Period';
    const perfCompareLabel = perfComparisonFilters.compare_term_id 
        ? (terms?.find(t => t.id.toString() === perfComparisonFilters.compare_term_id.toString())?.name || 'Comparison Term')
        : (perfComparisonFilters.compare_year_id ? 'Comparison Year' : 'Compare');

    return (
        <div className="min-h-screen bg-slate-50">
            <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6">
                {/* Header */}
                <header className="relative bg-[#18216D] rounded-[2rem] p-6 md:p-8 text-white shadow-2xl shadow-indigo-900/20">
                    <div className="absolute inset-0 overflow-hidden rounded-[2rem] pointer-events-none">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC425]/10 rounded-full -mr-32 -mt-32 blur-3xl pointer-events-none"></div>
                    </div>
                    <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#FFC425] mb-2">{is_principal ? 'School Analytics' : (stats?.name || 'Department Management')}</p>
                            <h1 className="text-3xl sm:text-5xl font-black tracking-tighter italic leading-none">
                                {(() => {
                                    const hour = new Date().getHours();
                                    if (hour >= 5 && hour < 12) return 'Good morning';
                                    if (hour >= 12 && hour < 17) return 'Good afternoon';
                                    if (hour >= 17 && hour < 21) return 'Good evening';
                                    return 'Welcome back';
                                })()}, <span className="text-[#FFC425]">{user?.first_name || 'HOD'}</span>
                            </h1>
                            {stats?.oversight_description && (!is_principal || selectedGradeId || selectedStreamId) && (
                                <p className="text-xs font-bold text-indigo-200 uppercase tracking-widest mt-4">
                                    {is_principal ? `Viewing: ${stats.oversight_description}` : stats.oversight_description}
                                </p>
                            )}
                            <div className="h-2 w-24 bg-[#FFC425] rounded-full mt-6"></div>

                            {pendingDisciplineCount > 0 && (
                                <button
                                    onClick={() => navigate('/teacher/discipline', { state: { tab: 'demerits', filter: 'escalated' } })}
                                    className="mt-5 inline-flex items-center gap-2.5 px-4 py-2 bg-amber-400/20 hover:bg-amber-400/30 text-[#FFC425] border border-[#FFC425]/40 rounded-2xl text-xs font-black uppercase tracking-wider backdrop-blur-sm transition-all shadow-lg active:scale-95 group"
                                >
                                    <ExclamationTriangleIcon className="w-4 h-4 text-[#FFC425] animate-pulse" />
                                    <span>{pendingDisciplineCount} Discipline Case{pendingDisciplineCount > 1 ? 's' : ''} Pending Review</span>
                                    <ArrowRightIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                                </button>
                            )}
                        </div>
                    </div>
                </header>

                {/* ── Standalone Filter Bar ── */}
                <div className="flex flex-wrap items-end justify-between gap-4 py-1">
                    <div className="flex flex-wrap items-end gap-4">
                        {/* Combined Grade & Stream — both HOD and Principal */}
                        {combinedGradeStreamOptions.length > 0 && (
                            <div className="flex flex-col gap-1">
                                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">Grade &amp; Stream</p>
                                <FilterDropdown
                                    value={combinedGradeStreamValue}
                                    onChange={handleCombinedChange}
                                    options={combinedGradeStreamOptions}
                                    placeholder="All Grades &amp; Streams"
                                    className="w-52"
                                />
                            </div>
                        )}

                        {/* Term Filter */}
                        <div className="flex flex-col gap-1">
                            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">Term</p>
                            <FilterDropdown
                                value={selectedTerm || ''}
                                onChange={(val) => setSelectedTerm(val)}
                                options={terms?.map(t => ({ name: t.name, value: t.id.toString() })) || []}
                                placeholder={terms?.find(t => t.is_current)?.name || 'Current Term'}
                                className="w-40"
                            />
                        </div>

                        {/* Assessment Filter */}
                        <div className="flex flex-col gap-1">
                            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">Assessment</p>
                            <FilterDropdown
                                value={assessmentFilter}
                                onChange={(val) => setAssessmentFilter(val || 'combined')}
                                options={[
                                    { name: 'Combined Average', value: 'combined' },
                                    { name: 'Mid-Term Exam', value: 'mid-term' },
                                    { name: 'End-Term Exam', value: 'end-term' },
                                    { name: 'Formative Assessments', value: 'formative' },
                                ]}
                                placeholder="Combined Average"
                                className="w-44"
                            />
                        </div>
                    </div>

                    {/* Clear Filters (when active) */}
                    {(selectedStreamId || selectedGradeId) && (
                        <button
                            onClick={() => {
                                setSelectedStreamId(null);
                                setSelectedGradeId(null);
                                setCurrentPage(1);
                            }}
                            className="px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider bg-white text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-all border border-slate-200 shadow-sm"
                        >
                            Clear Filters
                        </button>
                    )}
                </div>

                {/* Stats Grid - Parity with Class Teacher */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        label="Total students"
                        value={stats?.total_students || 0}
                        icon={UserGroupIcon}
                        color="indigo"
                        onClick={() => navigate('/teacher/class-roster', { state: { schoolLevelId: selectedLevelId, streamId: selectedStreamId, cachedRoster: students } })}
                    />
                    <StatCard
                        label="Avg Attendance"
                        value={`${stats?.attendance_rate || 0}%`}
                        icon={CheckCircleIcon}
                        badge={stats?.attendance_rate >= 90 ? 'Excellent' : stats?.attendance_rate >= 75 ? 'Good' : 'Needs Review'}
                        color="emerald"
                    />
                    <StatCard
                        label="Academic Avg"
                        value={stats?.performance_level || 'N/A'}
                        icon={ChartBarIcon}
                        badge={`${stats?.performance_avg || 0} Mean Score`}
                        color="amber"
                    />
                    <StatCard
                        label="Students at risk"
                        value={studentsRequiringAttention.length}
                        icon={ExclamationTriangleIcon}
                        color="rose"
                        onClick={() => {
                            const section = document.getElementById('students-at-risk-section');
                            if (section) section.scrollIntoView({ behavior: 'smooth' });
                        }}
                    />
                </section>

                {/* Main Content Grid */}
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Scholar Roster Table and Stream Performance Chart */}
                    <div className="lg:col-span-2 space-y-6 flex flex-col">
                        {/* Scholar Roster Table */}
                        <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden flex flex-col">
                        <div className="px-8 py-6 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
                            <div>
                                <h2 className="text-xl font-black text-[#18216D] uppercase tracking-tight">{is_principal ? 'School' : 'Department'} Roster</h2>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Cross-Grade Academic Management</p>
                            </div>
                            <div className="flex flex-col gap-3">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Search Scholars..."
                                        value={searchTerm}
                                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-xs font-bold text-[#18216D]"
                                    />
                                </div>
                                <div className="flex gap-2 w-full">
                                    <button
                                        onClick={() => navigate('/teacher/discipline')}
                                        className="flex-1 py-2.5 px-3 bg-[#FFC425] text-[#18216D] rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-[#FFD455] transition-all shadow-sm flex items-center justify-center gap-2 active:scale-95"
                                        title="Department Discipline Oversight"
                                    >
                                        <ScaleIcon className="w-4 h-4" />
                                        Discipline Oversight
                                    </button>
                                    <button
                                        onClick={() => navigate('/teacher/class-roster', { state: { schoolLevelId: selectedLevelId, streamId: selectedStreamId, cachedRoster: students } })}
                                        className="flex-1 py-2.5 px-3 bg-[#18216D] text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-900 transition-all shadow-sm flex items-center justify-center gap-2 active:scale-95"
                                    >
                                        <Squares2X2Icon className="w-4 h-4" />
                                        Grades View
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-[#F8FAFC]">
                                    <tr>
                                        <th className="px-8 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">Scholar</th>
                                        <th className="px-8 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">Grade/Unit</th>
                                        <th className="px-8 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">Attendance</th>
                                        <th className="px-8 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">Performance</th>
                                        <th className="px-8 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {paginatedStudents.length > 0 ? (
                                        paginatedStudents.map(student => (
                                            <tr 
                                                key={student.id} 
                                                className="hover:bg-slate-50/50 transition-colors cursor-pointer group"
                                                onClick={(e) => {
                                                    if(e.target.closest('button')) return;
                                                    navigate(`/teacher/student-detail/${student.id}`, { 
                                                        state: { 
                                                            studentIds: students.map(s => s.id),
                                                            returnUrl: location.pathname + location.search 
                                                        } 
                                                    });
                                                }}
                                            >
                                                <td className="px-8 py-5">
                                                    <div className="flex items-center gap-4">
                                                        <div className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-black text-[10px] group-hover:bg-indigo-600 group-hover:text-white transition-all">
                                                            {student.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <p className="font-black text-sm text-[#18216D]">{student.name}</p>
                                                                {student.house_name && (
                                                                    <span 
                                                                        className="text-[7px] font-black px-1.5 py-0.5 rounded-full text-white uppercase tracking-tighter"
                                                                        style={{ backgroundColor: student.house_color || '#18216D' }}
                                                                    >
                                                                        {student.house_name}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{student.student_id}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <div>
                                                        <p className="text-xs font-black text-[#18216D] uppercase">{student.grade}</p>
                                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{student.stream}</p>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex-1 max-w-[60px] h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                            <div
                                                                className={`h-full rounded-full ${student.attendance < 75 ? 'bg-rose-500' : 'bg-emerald-500'}`}
                                                                style={{ width: `${student.attendance}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className={`text-xs font-black ${student.attendance < 75 ? 'text-rose-600' : 'text-slate-600'}`}>
                                                            {student.attendance}%
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <div className="flex flex-col">
                                                        {(() => {
                                                            const levelStr = student.performance_level || 'N/A';
                                                            const lData = getLevelData(levelStr);
                                                            return (
                                                                <span className={`w-fit px-2 py-1 rounded-lg border text-[9px] font-black uppercase tracking-widest ${lData.color} ${lData.bg} ${lData.border}`}>
                                                                    {levelStr}
                                                                </span>
                                                            );
                                                        })()}
                                                        {student.performance > 0 && (
                                                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter mt-1 ml-1">{student.performance} MS</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-8 py-5 text-right flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => navigate(`/teacher/student-detail/${student.id}`, { 
                                                            state: { 
                                                                studentIds: students.map(s => s.id),
                                                                returnUrl: location.pathname + location.search 
                                                            } 
                                                        })}
                                                        className="p-2.5 bg-white border border-slate-100 text-[#18216D] rounded-xl hover:bg-indigo-50 hover:text-indigo-600 transition-all shadow-sm"
                                                        title="Deep Dive Analysis"
                                                    >
                                                        <EyeIcon className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExportStudentId(student.id);
                                                        }}
                                                        className="p-2.5 bg-white border border-slate-100 text-[#18216D] rounded-xl hover:bg-indigo-50 hover:text-indigo-600 transition-all shadow-sm"
                                                        title="Export Report PDF"
                                                    >
                                                        <DocumentArrowDownIcon className="w-5 h-5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="py-20 text-center">
                                                <UserGroupIcon className="w-16 h-16 text-slate-100 mx-auto mb-4" />
                                                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No matching scholars in {is_principal ? 'school' : 'department'}</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Bar */}
                        {totalPages > 1 && (
                            <div className="px-8 py-6 bg-[#F8FAFC] border-t border-slate-100 flex items-center justify-between">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                    Page {currentPage} of {totalPages}
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="p-2 bg-white border border-slate-100 rounded-xl text-slate-400 hover:text-[#18216D] disabled:opacity-30 transition-all shadow-sm"
                                    >
                                        <ChevronLeftIcon className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-2 bg-white border border-slate-100 rounded-xl text-slate-400 hover:text-[#18216D] disabled:opacity-30 transition-all shadow-sm"
                                    >
                                        <ChevronRightIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        )}
                        </div>
                    </div>

                    {/* Side Panel: Departments/Grades & Events */}
                    <div className="space-y-6">

                        {/* ── Compact Teacher Classes & Assessment Progress Status Widget (Placed above Events) ── */}
                        {!is_principal && (
                            <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 flex flex-col">
                                <div className="mb-4">
                                    <div className="flex items-center justify-between gap-2 mb-2.5">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <AcademicCapIcon className="w-5 h-5 text-[#FFC425] shrink-0" />
                                            <h3 className="text-sm font-black text-[#18216D] uppercase tracking-tight truncate">
                                                Classes &amp; Grading
                                            </h3>
                                        </div>
                                        <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200/60 shrink-0">
                                            {completedClassesCount}/{filteredTeacherClasses.length} Done
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => navigate('/teacher/classes-grading')}
                                        className="w-full py-1.5 px-3 bg-indigo-50/70 hover:bg-indigo-100 text-indigo-700 rounded-xl text-[10px] font-black uppercase tracking-wider flex items-center justify-between transition-all group active:scale-[0.99]"
                                        title="Open standalone Classes & Grading page"
                                    >
                                        <span>Open Full Management</span>
                                        <ArrowRightIcon className="w-3.5 h-3.5 text-indigo-400 group-hover:text-indigo-700 group-hover:translate-x-0.5 transition-all" />
                                    </button>
                                </div>

                                {teacherClassesLoading ? (
                                    <div className="py-8 flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#18216D]" />
                                    </div>
                                ) : filteredTeacherClasses.length === 0 ? (
                                    <div className="text-center py-8">
                                        <AcademicCapIcon className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No classes matching filter</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3 overflow-y-auto max-h-[360px] pr-1">
                                        {filteredTeacherClasses.map(cls => (
                                            <div
                                                key={cls.id}
                                                onClick={() => {
                                                    setModalBandCounts({ EE: 0, ME: 0, AE: 0, BE: 0 });
                                                    setSelectedClassForGradebook(cls);
                                                }}
                                                className="p-4 rounded-2xl border border-slate-100 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer bg-slate-50/50 hover:bg-white group"
                                            >
                                                <div className="flex items-start justify-between gap-3 mb-2">
                                                    <div className="min-w-0">
                                                        <p className="font-black text-xs text-[#18216D] group-hover:text-indigo-600 transition-colors truncate">
                                                            {cls.name}
                                                        </p>
                                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-0.5 truncate">
                                                            {cls.grade_level} • {cls.stream} • Teacher: <span className="text-[#18216D] font-black">{cls.teacher_name}</span>
                                                        </p>
                                                    </div>
                                                    <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full shrink-0 ${
                                                        cls.grading_progress_pct >= 100
                                                            ? 'bg-emerald-100 text-emerald-700'
                                                            : cls.grading_progress_pct > 0
                                                            ? 'bg-amber-100 text-amber-700'
                                                            : 'bg-rose-100 text-rose-700'
                                                    }`}>
                                                        {cls.status}
                                                    </span>
                                                </div>

                                                <div className="flex items-center justify-between text-[10px] font-bold text-slate-500 mb-1.5">
                                                    <span>{cls.total_marks_recorded ?? cls.graded_students} of {cls.total_marks_expected ?? cls.total_students} marks recorded</span>
                                                    <span className="font-black text-[#18216D]">{cls.grading_progress_pct}%</span>
                                                </div>

                                                <div className="w-full bg-slate-200/60 rounded-full h-1.5 overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all duration-500 ${
                                                            cls.grading_progress_pct >= 100 ? 'bg-emerald-500' : cls.grading_progress_pct >= 50 ? 'bg-[#FFC425]' : 'bg-rose-500'
                                                        }`}
                                                        style={{ width: `${cls.grading_progress_pct}%` }}
                                                    />
                                                </div>

                                                <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100 text-[9px] text-slate-400 font-bold">
                                                    <span>{cls.assessments_count} assessments created</span>
                                                    <span className="text-indigo-600 group-hover:translate-x-0.5 transition-transform flex items-center gap-1 font-black">
                                                        View GradeBook &rarr;
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Departmental Events Section ── */}
                        <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-black text-[#18216D] flex items-center uppercase tracking-tight">
                                    <CalendarIcon className="w-6 h-6 mr-3 text-[#FFC425]" />
                                    {is_principal ? 'School Events' : 'Departmental Events'}
                                </h3>
                            </div>
                            <div className="space-y-4">
                                {events.length > 0 ? (
                                    events.map(event => (
                                        <div key={event.id} className="p-4 bg-slate-50/50 rounded-2xl border border-slate-100 hover:bg-white transition-all">
                                            <p className="font-black text-sm text-[#18216D] tracking-tight">{event.title}</p>
                                            <div className="flex items-center gap-2 mt-2">
                                                <p className="text-[10px] font-black text-[#18216D] uppercase tracking-widest px-2 py-0.5 bg-slate-100 rounded">
                                                    {new Date(event.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                                </p>
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{event.time || 'All Day'}</p>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-12">
                                        <CalendarIcon className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">No upcoming events</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Stream Performance Chart */}
                <section className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6">
                        <div>
                            <h2 className="text-xl font-black text-[#18216D] tracking-tight italic">Performance by Stream</h2>
                            <p className="text-slate-400 font-bold text-xs mt-1 lowercase tracking-widest leading-none">
                                Average score per class stream
                            </p>
                        </div>
                        <CompareControls onFilterChange={setStreamComparisonFilters} />
                    </div>

                    <div className="flex items-center gap-6 mb-8 px-2">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-[#18216D]"></div>
                            <span className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">
                                {streamTargetLabel}
                            </span>
                        </div>
                        {isStreamComparisonEnabled && (
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded bg-[#FFC425]"></div>
                                <span className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">
                                    {streamCompareLabel}
                                </span>
                            </div>
                        )}
                    </div>

                    {(() => {
                        if (streamTargetLoading || streamCompareLoading) return (
                            <div className="h-64 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18216D]" />
                            </div>
                        );

                        if (streamChartData.length === 0) return (
                            <div className="h-64 flex flex-col items-center justify-center">
                                <ChartBarIcon className="w-10 h-10 text-slate-200 mb-3" />
                                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No stream data available</p>
                            </div>
                        );

                        return (
                            <div className="h-64 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={streamChartData} margin={{ top: 5, right: 10, left: 0, bottom: 60 }} barGap={8}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis
                                            dataKey="stream"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: '#64748b', fontSize: 8, fontWeight: 700 }}
                                            angle={-40}
                                            textAnchor="end"
                                            interval={0}
                                        />
                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: '#64748b', fontSize: 9, fontWeight: 700 }}
                                            domain={[0, 100]}
                                            tickFormatter={v => `${v}%`}
                                        />
                                        <Tooltip
                                            cursor={{ fill: 'rgba(24,33,109,0.03)' }}
                                            content={({ active, payload, label }) => {
                                                if (!active || !payload?.length) return null;
                                                const d = streamChartData.find(c => c.stream === label);
                                                return (
                                                    <div className="bg-white/95 backdrop-blur-md p-4 rounded-3xl shadow-2xl border border-slate-100 min-w-[160px] pointer-events-none">
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">{label}</p>
                                                        <div className="space-y-3">
                                                            <div className="flex items-center justify-between gap-4">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-2 h-2 rounded-full bg-[#18216D]"></div>
                                                                    <span className="text-[11px] font-black text-[#18216D] uppercase">{streamTargetLabel}</span>
                                                                </div>
                                                                <span className="text-xl font-black text-[#18216D] italic">
                                                                    {d.has_target ? `${d.target_score}%` : '--'}
                                                                </span>
                                                            </div>
                                                            {isStreamComparisonEnabled && (
                                                                <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-50">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="w-2 h-2 rounded-full bg-[#FFC425]"></div>
                                                                        <span className="text-[11px] font-black text-[#18216D] uppercase">{streamCompareLabel}</span>
                                                                    </div>
                                                                    <span className="text-xl font-black text-[#18216D] italic">
                                                                        {d.has_compare ? `${d.compare_score}%` : '--'}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                        />
                                        <Bar dataKey="target_score" fill="#18216D" radius={[6, 6, 0, 0]} maxBarSize={40} />
                                        {isStreamComparisonEnabled && (
                                            <Bar dataKey="compare_score" fill="#FFC425" radius={[6, 6, 0, 0]} maxBarSize={40} />
                                        )}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        );
                    })()}
                </section>


                {/* ── Pie Chart + Academic Risk: two-column layout ─── */}
                <section className="grid grid-cols-1 xl:grid-cols-5 gap-6">

                    {/* Grade Band Distribution (Pie Chart) (40% = 2 cols) */}
                    <div className="xl:col-span-2 bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 flex flex-col">
                        <div className="mb-6">
                            <h2 className="text-xl font-black text-[#18216D] tracking-tight italic">Grade Distribution</h2>
                            <p className="text-slate-400 font-bold text-xs mt-1 lowercase tracking-widest leading-none">
                                Performance bands distribution
                            </p>
                        </div>
                        
                        {pieChartData.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center py-8">
                                <ChartBarIcon className="w-10 h-10 text-slate-200 mb-3" />
                                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest text-center">No data available</p>
                            </div>
                        ) : (
                            <>
                                <div className="h-48 w-full mb-6 relative">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (!active || !payload?.length) return null;
                                                    const data = payload[0].payload;
                                                    return (
                                                        <div className="bg-white/95 backdrop-blur-md p-3 rounded-2xl shadow-xl border border-slate-100 pointer-events-none">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: data.color }}></div>
                                                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{data.name}</span>
                                                            </div>
                                                            <p className="text-sm font-black text-[#18216D]">{data.value} students</p>
                                                        </div>
                                                    );
                                                }}
                                            />
                                            <Pie
                                                data={pieChartData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={85}
                                                paddingAngle={4}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {pieChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none flex-col">
                                        <p className="text-2xl font-black text-[#18216D] leading-none">{students.filter(s => s.performance_level).length}</p>
                                        <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1">Total</p>
                                    </div>
                                </div>
                                
                                <div className="space-y-2 overflow-y-auto pr-1 flex-1">
                                    {pieChartData.map((band) => (
                                        <div key={band.name} className={`rounded-xl border ${band.borderClass} overflow-hidden transition-all duration-300`}>
                                            <button 
                                                onClick={() => setExpandedBands(prev => ({...prev, [band.name]: !prev[band.name]}))}
                                                className={`w-full flex items-center justify-between p-3 ${band.bgClass} hover:bg-opacity-80 transition-all`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: band.color }}></div>
                                                    <div className="text-left">
                                                        <p className={`text-[10px] font-black uppercase tracking-widest ${band.textClass}`}>{band.name}</p>
                                                        <p className="text-[8px] font-bold text-slate-500 tracking-tighter truncate max-w-[120px]">{band.label}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-xs font-black ${band.textClass}`}>{band.value}</span>
                                                    {expandedBands[band.name] ? 
                                                        <ChevronUpIcon className={`w-3 h-3 ${band.textClass}`} /> : 
                                                        <ChevronDownIcon className={`w-3 h-3 ${band.textClass}`} />
                                                    }
                                                </div>
                                            </button>
                                            
                                            {/* Sub-bands accordion */}
                                            <div 
                                                className={`transition-all duration-300 ease-in-out ${expandedBands[band.name] ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}
                                            >
                                                <div className="p-3 pt-0 bg-white grid grid-cols-2 gap-2">
                                                    {Object.entries(band.subbands).filter(([_, count]) => count > 0).sort((a, b) => a[0].localeCompare(b[0])).map(([subcode, count]) => (
                                                        <div key={subcode} className="flex items-center justify-between bg-slate-50 p-2 rounded-lg border border-slate-100">
                                                            <span className="text-[10px] font-black text-[#18216D] uppercase">{subcode}</span>
                                                            <span className="text-[10px] font-bold text-slate-500">{count}</span>
                                                        </div>
                                                    ))}
                                                    {Object.values(band.subbands).every(count => count === 0) && (
                                                        <div className="col-span-2 text-center py-2 text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                                                            No specific breakdown
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Students at risk (60% = 3 cols) */}
                    <div id="students-at-risk-section" className="xl:col-span-3 bg-white rounded-[2.5rem] shadow-sm border border-rose-100 p-8 flex flex-col">
                        <div className="flex items-center gap-3 mb-6">
                            <ExclamationTriangleIcon className="h-5 w-5 text-rose-500 shrink-0" />
                            <h3 className="text-base font-black text-[#18216D] uppercase tracking-tight flex-1">
                                Students at risk
                            </h3>
                            {/* Grid / Table toggle */}
                            <div className="flex bg-slate-100 p-0.5 rounded-xl">
                                <button
                                    onClick={() => setRiskViewMode('grid')}
                                    className={`p-1.5 rounded-lg transition-all ${
                                        riskViewMode === 'grid'
                                            ? 'bg-white text-[#18216D] shadow-sm'
                                            : 'text-slate-400 hover:text-slate-600'
                                    }`}
                                    title="Grid view"
                                >
                                    <Squares2X2Icon className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    onClick={() => setRiskViewMode('table')}
                                    className={`p-1.5 rounded-lg transition-all ${
                                        riskViewMode === 'table'
                                            ? 'bg-white text-[#18216D] shadow-sm'
                                            : 'text-slate-400 hover:text-slate-600'
                                    }`}
                                    title="Table view"
                                >
                                    <ListBulletIcon className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>

                        {studentsRequiringAttention.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center py-8">
                                <CheckCircleIcon className="w-10 h-10 text-emerald-200 mb-3" />
                                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest text-center">No students at risk</p>
                            </div>
                        ) : riskViewMode === 'grid' ? (
                            <div className="grid grid-cols-1 gap-3 overflow-y-auto max-h-[32rem] pr-1">
                                {studentsRequiringAttention.map(student => {
                                    const isAttendance = student.attendance < 75;
                                    const issueLabel = isAttendance
                                        ? `Low Attendance: ${student.attendance}%`
                                        : `${student.performance_level || 'BE'} (${Math.round(student.performance || 0)}%)`;
                                    return (
                                        <div
                                            key={student.id}
                                            className="p-4 bg-rose-50/30 rounded-2xl border border-rose-100 group hover:bg-rose-50 transition-all cursor-pointer flex items-center justify-between"
                                            onClick={() => navigate(`/teacher/student-detail/${student.id}`)}
                                        >
                                            <div className="min-w-0">
                                                <p className="font-black text-xs text-[#18216D] truncate">{student.name}</p>
                                                <p className="text-[10px] text-rose-600 font-bold uppercase tracking-tight mt-0.5">{issueLabel}</p>
                                            </div>
                                            <EyeIcon className="w-4 h-4 text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="overflow-x-auto overflow-y-auto max-h-[32rem] -mx-2">
                                <table className="w-full text-left">
                                    <thead className="bg-slate-50 sticky top-0">
                                        <tr>
                                            <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Scholar</th>
                                            <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Admission</th>
                                            <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Class</th>
                                            <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Issue</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {studentsRequiringAttention.map(student => {
                                            const isAttendance = student.attendance < 75;
                                            const issueLabel = isAttendance
                                                ? `Low Attendance: ${student.attendance}%`
                                                : `${student.performance_level || 'BE'} (${Math.round(student.performance || 0)}%)`;
                                            return (
                                                <tr
                                                    key={student.id}
                                                    className="hover:bg-rose-50/30 transition-colors cursor-pointer group"
                                                    onClick={() => navigate(`/teacher/student-detail/${student.id}`)}
                                                >
                                                    <td className="px-4 py-3.5">
                                                        <p className="font-black text-xs text-[#18216D] truncate">{student.name}</p>
                                                    </td>
                                                    <td className="px-4 py-3.5">
                                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{student.student_id}</p>
                                                    </td>
                                                    <td className="px-4 py-3.5">
                                                        <p className="text-[10px] font-bold text-slate-500">
                                                            {student.grade}{student.stream && student.stream !== 'N/A' ? ` – ${student.stream}` : ''}
                                                        </p>
                                                    </td>
                                                    <td className="px-4 py-3.5">
                                                        <span className={`text-[10px] font-black uppercase tracking-tight px-2 py-0.5 rounded-lg ${
                                                            isAttendance
                                                                ? 'bg-amber-50 text-amber-600 border border-amber-100'
                                                                : 'bg-rose-50 text-rose-600 border border-rose-100'
                                                        }`}>
                                                            {issueLabel}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {studentsRequiringAttention.length > 0 && (
                            <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest mt-4 text-right">
                                {studentsRequiringAttention.length} students flagged
                            </p>
                        )}
                    </div>
                </section>

                {/* Performance Comparison Chart Section */}
                <section className="pb-10 px-0">
                    <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-sm border border-slate-100">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                            <div>
                                <h2 className="text-2xl font-black text-[#18216D] tracking-tight italic">Performance Comparison</h2>
                                <p className="text-slate-400 font-bold text-xs mt-1 lowercase tracking-widest leading-none">
                                    {isPerfComparisonEnabled 
                                        ? `Comparative ${is_principal ? 'school' : 'departmental'} performance across strands`
                                        : `Class-wide mean score distribution — ${perfTargetLabel}`
                                    }
                                </p>
                            </div>
                            <CompareControls onFilterChange={setPerfComparisonFilters} />
                        </div>

                        <div className="flex items-center gap-6 mb-8 px-2">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded bg-[#18216D]"></div>
                                <span className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">
                                    {perfTargetLabel}
                                </span>
                            </div>
                            {isPerfComparisonEnabled && (
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded bg-[#FFC425]"></div>
                                    <span className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">
                                        {perfCompareLabel}
                                    </span>
                                </div>
                            )}
                            <div className="ml-auto flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded border border-dashed border-slate-300"></div>
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic leading-none">Skeleton (No Data)</span>
                                </div>
                            </div>
                        </div>

                        <div className="h-[400px] w-full">
                            {perfTargetLoading || perfCompareLoading ? (
                                <div className="h-full w-full flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18216D]"></div>
                                </div>
                            ) : chartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={chartData}
                                        margin={{ top: 10, right: 20, left: 10, bottom: 90 }}
                                        barGap={8}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis
                                            dataKey="learning_area"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: '#64748b', fontSize: 9, fontWeight: 700 }}
                                            angle={-40}
                                            textAnchor="end"
                                            interval={0}
                                        />
                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: '#64748b', fontSize: 10, fontWeight: 700 }}
                                            domain={[0, 100]}
                                            tickFormatter={v => `${v}%`}
                                        />
                                        <Tooltip
                                            cursor={{ fill: 'rgba(24, 33, 109, 0.03)' }}
                                            content={({ active, payload, label }) => {
                                                if (active && payload && payload.length) {
                                                    const d = chartData.find(c => c.learning_area === label);
                                                    return (
                                                        <div className="bg-white/95 backdrop-blur-md p-4 rounded-3xl shadow-2xl border border-slate-100 min-w-[160px] pointer-events-none">
                                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">{label}</p>
                                                            <div className="space-y-3">
                                                                <div className="flex items-center justify-between gap-4">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="w-2 h-2 rounded-full bg-[#18216D]"></div>
                                                                        <span className="text-[11px] font-black text-[#18216D] uppercase">{perfTargetLabel}</span>
                                                                    </div>
                                                                    <span className="text-xl font-black text-[#18216D] italic">
                                                                        {d.has_target ? `${d.target_score}% (${calculate8Level(d.target_score, performanceBands)})` : '--'}
                                                                    </span>
                                                                </div>
                                                                {isPerfComparisonEnabled && (
                                                                    <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-50">
                                                                        <div className="flex items-center gap-2">
                                                                            <div className="w-2 h-2 rounded-full bg-[#FFC425]"></div>
                                                                            <span className="text-[11px] font-black text-[#18216D] uppercase">{perfCompareLabel}</span>
                                                                        </div>
                                                                        <span className="text-xl font-black text-[#18216D] italic">
                                                                            {d.has_compare ? `${d.compare_score}% (${calculate8Level(d.compare_score, performanceBands)})` : '--'}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Bar 
                                            dataKey="target_score" 
                                            radius={[6, 6, 0, 0]} 
                                            barSize={isPerfComparisonEnabled ? 20 : 35}
                                        >
                                            {chartData.map((entry, index) => (
                                                <Cell 
                                                    key={`cell-target-${index}`} 
                                                    fill={entry.has_target ? '#18216D' : 'transparent'} 
                                                    stroke={entry.has_target ? 'none' : '#e2e8f0'}
                                                    strokeDasharray={entry.has_target ? '0' : '4 4'}
                                                />
                                            ))}
                                        </Bar>
                                        {isPerfComparisonEnabled && (
                                            <Bar 
                                                dataKey="compare_score" 
                                                radius={[6, 6, 0, 0]} 
                                                barSize={20}
                                            >
                                                {chartData.map((entry, index) => (
                                                    <Cell 
                                                        key={`cell-compare-${index}`} 
                                                        fill={entry.has_compare ? '#FFC425' : 'transparent'} 
                                                        stroke={entry.has_compare ? 'none' : '#e2e8f0'}
                                                        strokeDasharray={entry.has_compare ? '0' : '4 4'}
                                                    />
                                                ))}
                                            </Bar>
                                        )}
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full w-full flex flex-col items-center justify-center bg-slate-50/50 rounded-3xl border border-dashed border-slate-100">
                                    <ChartBarIcon className="w-12 h-12 text-slate-200 mb-3" />
                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest italic">Insufficient performance data for visualization</p>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </main>

            <ExportReportModal
                studentId={exportStudentId}
                isOpen={!!exportStudentId}
                onClose={() => setExportStudentId(null)}
            />

            {/* GradeBook Inspection Modal for HOD */}
            {selectedClassForGradebook && (
                <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 sm:p-6 pt-16 sm:pt-20 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-[2.5rem] w-full max-w-7xl max-h-[92vh] shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
                        <div className="px-8 py-5 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-50/70">
                            <div>
                                <div className="flex items-center gap-3">
                                    <h3 className="text-xl font-black text-[#18216D] uppercase tracking-tight">
                                        {selectedClassForGradebook.name}
                                    </h3>
                                    <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-[#18216D] text-white">
                                        {selectedClassForGradebook.grade_level} • {selectedClassForGradebook.stream}
                                    </span>
                                </div>
                                <p className="text-xs font-bold text-slate-400 mt-1">
                                    Teacher: <span className="text-[#18216D]">{selectedClassForGradebook.teacher_name}</span> • Progress: <span className="text-emerald-600 font-black">{selectedClassForGradebook.grading_progress_pct}%</span> ({selectedClassForGradebook.graded_students}/{selectedClassForGradebook.total_students} students graded)
                                </p>
                            </div>

                            <div className="flex items-center gap-4">
                                {/* 4 Competency Band Metrics (Canonical cbcUtils.js palette) */}
                                <div className="flex items-center space-x-2">
                                    <div className="text-center bg-[#FFD700]/10 px-3.5 py-1.5 rounded-2xl border border-[#FFD700]/30 shadow-xs min-w-[56px]">
                                        <div className="text-base font-black text-[#B8860B] leading-none">{modalBandCounts.EE || 0}</div>
                                        <div className="text-[10px] font-black text-[#B8860B] uppercase mt-0.5 tracking-wider">EE</div>
                                    </div>
                                    <div className="text-center bg-[#40E0D0]/10 px-3.5 py-1.5 rounded-2xl border border-[#40E0D0]/30 shadow-xs min-w-[56px]">
                                        <div className="text-base font-black text-[#008B8B] leading-none">{modalBandCounts.ME || 0}</div>
                                        <div className="text-[10px] font-black text-[#008B8B] uppercase mt-0.5 tracking-wider">ME</div>
                                    </div>
                                    <div className="text-center bg-[#E97451]/10 px-3.5 py-1.5 rounded-2xl border border-[#E97451]/30 shadow-xs min-w-[56px]">
                                        <div className="text-base font-black text-[#C0392B] leading-none">{modalBandCounts.AE || 0}</div>
                                        <div className="text-[10px] font-black text-[#C0392B] uppercase mt-0.5 tracking-wider">AE</div>
                                    </div>
                                    <div className="text-center bg-[#808080]/10 px-3.5 py-1.5 rounded-2xl border border-[#808080]/30 shadow-xs min-w-[56px]">
                                        <div className="text-base font-black text-[#505050] leading-none">{modalBandCounts.BE || 0}</div>
                                        <div className="text-[10px] font-black text-[#505050] uppercase mt-0.5 tracking-wider">BE</div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => setSelectedClassForGradebook(null)}
                                    className="p-2 rounded-xl bg-white border border-slate-100 hover:bg-slate-50 text-slate-400 hover:text-slate-600 transition-all shadow-sm"
                                >
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30">
                            <GradeBook
                                courseId={selectedClassForGradebook.id || selectedClassForGradebook.course_id}
                                selectedTerm={selectedTerm}
                                onBandCountsChange={setModalBandCounts}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* AI Assistant Floating Drawer */}
            <TeacherAiAssistantDrawer
                dashboardType={is_principal ? "principal" : "hod"}
                schoolLevelId={selectedLevelId}
                termId={selectedTerm}
                gradeName={stats?.name || "Department"}
                courseName={is_principal ? "Principal Leadership Assistant" : "HOD Assistant"}
                activeTab={is_principal ? "principal_dashboard" : "hod_dashboard"}
            />
        </div>
    );
};

export default HodDashboard;
